import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../config/defaultLimit';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { CDRTransactionInterface } from '../../../interface/payment-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableAccountPayoutTransactionFilterApplied,
  fetchAccountPayoutTransactions,
  fetchAccountPayoutTransactionFilters,
  setSortData,
} from '../../../redux/slices/AccountPayoutTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setSidePanelData } from '../../../redux/slices/TableSidePanelSlice';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import CpoCdrSidePanel from '../../../shared-components/table-side-panel/CpoCdrSidePanel';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';
import TariffSidePanel from '../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatDuration from '../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import cpoTransactionApi from '../../../utils/api/cpo-transaction';

interface TransactionAccountProperties {
  accountCustomId?: string;
}

/**
 * TransactionAccount component for viewing partner-scoped CDR transactions.
 * @param {TransactionAccountProperties} properties - The component properties.
 * @returns {React.JSX.Element} The rendered component.
 */
function TransactionAccount({
  accountCustomId: propertyAccountCustomId,
}: Readonly<TransactionAccountProperties>): React.JSX.Element {
  const { t, i18n } = useTranslation(['transaction', 'sidePanel']);
  const dispatch = useAppDispatch();
  const { portalType } = usePortal();
  const isPartnerPortal = portalType === PortalType.PARTNER;
  const { id } = useParams<{ id: string }>();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const accountCustomId = propertyAccountCustomId || id || searchParameters.get('accountCustomId');

  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.accountPayoutTransaction);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });

  /**
   * Clears the date range values.
   * @returns {void}
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder={t('datePickerPlaceholder')}
          defaultPreset="last14Days"
        />
      </div>
    );
  };

  /**
   * Fetch the payment details in redux.
   * @param {any} data - The query payload for fetching transactions.
   * @returns {void}
   */
  const fetchPayments = (data: any) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading && accountCustomId) {
      dispatch(
        fetchAccountPayoutTransactions({
          accountCustomId: [accountCustomId],
          ...data,
        }),
      );
    }
  };

  /**
   * Function to refresh data.
   * @returns {void}
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchPayments({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: payments,
    });

  const column = [
    {
      label: t('CDR ID'),
      accessorKey: 'cdrId' as keyof CDRTransactionInterface,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <CpoCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('Session ID'),
      accessorKey: 'sessionId' as keyof CDRTransactionInterface,
      queryParamsKey: 'transactionObjectId',
    },
    {
      label: t('OCPPTransactionid'),
      accessorKey: 'transactionId' as keyof CDRTransactionInterface,
    },
    {
      label: t('Account ID'),
      accessorKey: 'accountCustomId' as keyof CDRTransactionInterface,
      sortAllow: true,
      ...(!isPartnerPortal
        ? {
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
          }
        : {}),
    },
    {
      label: t('Charging Point'),
      accessorKey: 'chargingPointSn' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('Location ID'),
      accessorKey: 'cdrLocation' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'id',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Location ID custom formatter.
       * @param {CDRTransactionInterface} row - Row data.
       * @returns {React.JSX.Element} Location ID element.
       */
      customData: (row: CDRTransactionInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.cdrLocation?.id) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('stationId', row.cdrLocation.id);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.id || '-'}
        </span>
      ),
    },
    {
      label: t('EVSE UID'),
      accessorKey: 'evseUid' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * EVSE UID custom formatter.
       * @param {CDRTransactionInterface} row - Row data.
       * @returns {React.JSX.Element} EVSE UID element.
       */
      customData: (row: CDRTransactionInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.chargingPointSn) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('deviceId', row.chargingPointSn);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.evseUid || '-'}
        </span>
      ),
    },
    {
      label: t('connectorId'),
      accessorKey: 'connectorId' as keyof CDRTransactionInterface,
      /**
       * Connector ID custom formatter.
       * @param {CDRTransactionInterface} row - Row data.
       * @returns {React.JSX.Element} Connector ID element.
       */
      customData: (row: CDRTransactionInterface) => (
        <span>{row.cdrLocation?.connectorId || '-'}</span>
      ),
    },
    {
      label: t('tariffName'),
      accessorKey: 'tariffs' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
      disableNewTab: true,
      /**
       * Tariff name custom formatter.
       * @param {CDRTransactionInterface} row - Row data.
       * @returns {React.JSX.Element} Tariff name element.
       */
      customData: (row: CDRTransactionInterface) => {
        const tariff = row.tariffs?.[0];
        const tariffId = tariff?.tariffId || tariff?._id;
        const tariffName = tariff?.name || '-';

        if (!tariffId || tariffName === '-') {
          return <span>{tariffName}</span>;
        }

        return (
          <span
            className="table-row-each-data hyperlink"
            role="presentation"
            onClick={(event) => {
              event.stopPropagation();
              const parameters = new URLSearchParams(searchParameters);
              const sidePanelKeys = [
                'accountCustomId',
                'deviceId',
                'connectorDeviceId',
                'stationId',
                'facilityId',
                'userCustomId',
                'userId',
                'utilityId',
                'transactionObjectId',
                'roleId',
                'sessionId',
                'cdrObjectId',
                'paymentDetailsId',
                'personalChargerId',
                'tariffId',
                'action',
              ];
              sidePanelKeys.forEach((key) => parameters.delete(key));
              parameters.set('tariffId', tariffId);
              setSearchParameters(parameters);
              dispatch(
                setSidePanelData({
                  key: 'Transactions',
                  value: row.id,
                }),
              );
            }}>
            {tariffName}
          </span>
        );
      },
    },
    {
      label: t('Energy (kWh)'),
      accessorKey: 'totalEnergy' as keyof CDRTransactionInterface,
      /**
       * Energy custom formatter.
       * @param {CDRTransactionInterface} row - Row data.
       * @returns {React.JSX.Element} Energy element.
       */
      customData: (row: CDRTransactionInterface) => (
        <span>{ToFixedValue(row.totalEnergy, 3) || '0.000'} kWh</span>
      ),
    },
    {
      label: t('Total Cost'),
      accessorKey: 'totalCost' as keyof CDRTransactionInterface,
      /**
       * Total cost custom formatter.
       * @param {CDRTransactionInterface} row - Row data.
       * @returns {React.JSX.Element} Formatted total cost element.
       */
      customData: (row: CDRTransactionInterface) => (
        <span>
          {row.totalCost?.inclVat !== undefined ? ToFixedValue(row.totalCost.inclVat) : '0.00'}{' '}
          {row.currency}
        </span>
      ),
    },
    {
      label: t('Total Time'),
      accessorKey: 'totalTime' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface) => (
        <span>{FormatDuration(rowData.totalTime)}</span>
      ),
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData.createdAt)}</span>
      ),
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData.updatedAt)}</span>
      ),
    },
  ];

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Column definitions for the table export.
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('CDR ID'), accessorKey: 'cdrId' },
    { label: t('Session ID'), accessorKey: 'sessionId' },
    { label: t('OCPPTransactionid'), accessorKey: 'transactionId' },
    { label: t('Account ID'), accessorKey: 'accountCustomId' },
    { label: t('Charging Point'), accessorKey: 'chargingPointSn' },
    { label: t('Location ID'), accessorKey: 'locationId' },
    { label: t('EVSE UID'), accessorKey: 'evseUid' },
    { label: t('connectorId'), accessorKey: 'connector' },
    { label: t('tariffName'), accessorKey: 'tariff' },
    { label: t('Energy (kWh)'), accessorKey: 'totalEnergy' },
    { label: t('Total Cost'), accessorKey: 'totalCost' },
    { label: t('Total Time'), accessorKey: 'totalTime' },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Table pagination function.
   * @param {any} value - Pagination value.
   * @returns {void}
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchPayments({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  /**
   * Filtering columns for export without hidden columns.
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function.
   * @param {string} type - The export format type ('csv' or 'pdf').
   * @returns {void}
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (!accountCustomId) return;
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
      accountCustomId: [accountCustomId],
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    const apiCall = cpoTransactionApi.fetchCpoTransactionsExport(payload);

    apiCall
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: CDRTransactionInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'cdrId':
                return row.cdrId || t('na');
              case 'sessionId':
                return row.sessionId || t('na');
              case 'transactionId':
                return row.transactionId || t('na');
              case 'accountCustomId':
                return row.accountCustomId || t('na');
              case 'chargingPointSn':
                return row.chargingPointSn || t('na');
              case 'locationId':
                return row.cdrLocation?.id || '-';
              case 'evseUid':
                return row.cdrLocation?.evseUid || '-';
              case 'connector':
                return row.cdrLocation?.connectorId || '-';
              case 'tariff':
                return row.tariffs?.[0]?.name || '-';
              case 'totalEnergy':
                return ToFixedValue(row.totalEnergy) ?? '0.00';
              case 'totalCost':
                return row.totalCost?.inclVat !== undefined
                  ? `${ToFixedValue(row.totalCost.inclVat)} ${row.currency}`
                  : `0.00 ${row.currency ?? ''}`;
              case 'totalTime':
                return FormatDuration(row.totalTime);
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt);
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt);
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'partner-transactions-list.csv');
          } else {
            exportPDF(headers, rows, 'partner-transactions-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function.
   * @param {any} sortData - The sortData object containing sortField and sortBy.
   * @returns {void}
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPayments({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const previousQueryKey = useRef<string | null>(null);

  useEffect(() => {
    const queryKey = `${accountCustomId}|${dateRange.startDate}|${dateRange.endDate}`;
    const isQueryKeyChanged = previousQueryKey.current !== queryKey;
    previousQueryKey.current = queryKey;

    // Fetch only when the account/date range changed or the user applied/cleared a filter.
    // The filter list thunk also replaces `filters`, which must not re-trigger the list API.
    if (isFilterUpdated || isQueryKeyChanged) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPayments({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
        sortBy,
        sortField,
      });
      dispatch(disableAccountPayoutTransactionFilterApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate, accountCustomId]);

  useEffect(() => {
    if (accountCustomId) {
      dispatch(fetchAccountPayoutTransactionFilters({ accountCustomId: [accountCustomId] }));
    }
  }, [accountCustomId]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('userCustomId') ||
      searchParameters.get('transactionObjectId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('userCustomId');
      parameters.delete('transactionObjectId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <CustomTable
        dataKey="Transactions"
        version="v2"
        header={column}
        data={payments}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        sizeChangeButtonRequired
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        exportDataPdfOrCSV={
          permissionCheck('cpoTransactionExport') || permissionCheck('partnerTransactionExport')
            ? exportDataPdfOrCSV
            : undefined
        }
        exportLoader={exportLoader}
        hoverLabel={t('refresh')}
        rightTopContent={renderDateFilters()}
        onClearFilterChange={() => {
          clearDateRange();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={handleSidePanelClose}
        height={600}
      />
    </div>
  );
}

TransactionAccount.defaultProps = {
  accountCustomId: '',
};

export default TransactionAccount;
